import {HttpErrorResponse} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {ErrorHandlerUtils} from '@app/core/utils/error-handler.utils';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {TranslateService} from '@ngx-translate/core';
import {MessageService} from 'primeng/api';
import {catchError, map, of, switchMap} from 'rxjs';
import {IMultiSelectResponse, ISelectResponse} from '../../models/dynamic-form.model';
import {DynamicFormService} from '../../services/dynamic-form.service';
import {
  DynamicFormActions,
  multiSelectAction,
  multiSelectEffectError,
  multiSelectEffectFailure,
  multiSelectEffectSuccess,
  selectAction,
  selectEffectError,
  selectEffectFailure,
  selectEffectSuccess,
} from './dynamic-form.actions';

@Injectable()
export class DynamicFormEffects {
  private readonly actions$ = inject(Actions);
  private readonly dynamicFormService = inject(DynamicFormService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  globalToast = 'global-toast';

  multiSelect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DynamicFormActions.MultiSelect),
      switchMap((action: ReturnType<typeof multiSelectAction>) => this.multiSelect(action))
    )
  );

  private multiSelect(action: ReturnType<typeof multiSelectAction>) {
    return this.dynamicFormService.getMultiSelectOptions(action.request).pipe(
      map((response: IMultiSelectResponse) => {
        return multiSelectEffectSuccess({
          response,
          formControlName: action.formControlName,
        });
      }),
      catchError((error: HttpErrorResponse) => {
        const errMsg = ErrorHandlerUtils.getErrorMessage(this.translate, error.status);
        ErrorHandlerUtils.showErrorMessage(this.messageService, this.globalToast, errMsg);
        if (Array.isArray(error.error)) {
          return of(
            multiSelectEffectFailure({
              reason: error.error.join(', '),
              formControlName: action.formControlName,
            })
          );
        } else if (typeof error.error === 'string') {
          return of(
            multiSelectEffectFailure({
              reason: error.error,
              formControlName: action.formControlName,
            })
          );
        } else {
          return of(
            multiSelectEffectError({
              error: error.error,
              formControlName: action.formControlName,
            })
          );
        }
      })
    );
  }

  select$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DynamicFormActions.Select),
      switchMap((action: ReturnType<typeof selectAction>) => this.select(action))
    )
  );

  private select(action: ReturnType<typeof selectAction>) {
    return this.dynamicFormService.getSelectOptions(action.request).pipe(
      map((response: ISelectResponse[]) => {
        const excludedOptions = action.request.excludedOptionLabels.toLocaleLowerCase().split(';');
        response = response.filter(x => !excludedOptions.includes(x.name.toLocaleLowerCase()));
        return selectEffectSuccess({
          response,
          formControlName: action.formControlName,
        });
      }),
      catchError((error: HttpErrorResponse) => {
        const errMsg = ErrorHandlerUtils.getErrorMessage(this.translate, error.status);
        ErrorHandlerUtils.showErrorMessage(this.messageService, this.globalToast, errMsg);
        if (Array.isArray(error.error)) {
          return of(
            selectEffectFailure({
              reason: error.error.join(', '),
              formControlName: action.formControlName,
            })
          );
        } else if (typeof error.error === 'string') {
          return of(
            selectEffectFailure({
              reason: error.error,
              formControlName: action.formControlName,
            })
          );
        } else {
          return of(
            selectEffectError({
              error: error.error,
              formControlName: action.formControlName,
            })
          );
        }
      })
    );
  }
}
